import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Grid, Typography } from '@material-ui/core'
import { categoriesRequest } from 'src/store/ducks/categories/actions'
import { RootState } from 'src/store/ducks/rootReducer'
import { postsRequest } from 'src/store/ducks/news/actions'
import sortByDate from 'src/utils/sortByDate'
import { PUBLISHED_STATUS } from 'src/components/NewsModule/constants'
import usePersistedState from 'src/hooks/usePersistedState'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import HorizontalNewsCardSkeleton from 'src/components/Skeleton/News/HorizontalNewsCardSkeleton/HorizontalNewsCardSkeleton'
import HorizontalNewsCard from 'src/components/HorizontalNewsCard/HorizontalNewsCard'
import ExpandedNewsItemCard from 'src/components/ExpandedNewsItemCard/ExpandedNewsItemCard'
import ExpandedSkeletonNewsCard from 'src/components/Skeleton/News/ExpandedSkeletonNewsCard/ExpandedSkeletonNewsCard'
import getCategoryRouteByCategoryId from 'src/utils/News/getCategoryRouteByCategoryId'
import SearchInput from 'src/components/SearchInput/SearchInput'
import { useStyles } from './NewsListedByCategoryContent.styles'

const NewsListedByCategoryContent = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [user] = usePersistedState('auth-token', '')

  const { news } = useSelector((state: RootState) => state.news)
  const [indexLimitPost, setIndexLimitPost] = useState<number>(4)
  const [postList, setPostList] = useState<any>([])

  const { categories } = useSelector((state: RootState) => state.categories)
  const [categoryId, setCategoryId] = useState<any>()
  const categoryUrl = location.pathname.split('/')[2]

  const refSentinel = useRef<HTMLDivElement>(null)
  const refObserver = useRef<IntersectionObserver | null>(null)

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  useEffect(() => {
    dispatch(postsRequest())
    dispatch(categoriesRequest())
  }, [])

  useEffect(() => {
    if (categories.length !== 0 && !categoryId) {
      const categoryMap: Record<string, string> = {
        blog: 'Blog',
        publications: 'Publicação',
        events: 'Evento',
        awards: 'Prêmio',
        defenses: 'Defesa'
      }
      setCategoryId(
        categories.find((e) => e.title === categoryMap[categoryUrl]) || ''
      )
    }
  }, [isLoading, categories, categoryUrl])

  useEffect(() => {
    if (news.length !== 0 && postList.length === 0 && categoryId) {
      const filtered = sortByDate(news, PUBLISHED_STATUS).filter((post) => {
        const isSameCategory = post.newsCategoriesId === categoryId.id
        const isPublished = post.status === PUBLISHED_STATUS
        if (!isSameCategory || !isPublished) return false
        return user ? true : post.audience === 'Externo'
      })
      setPostList(filtered)
      setIsLoading(false)
    }
  }, [news, categoryId, user])

  useEffect(() => {
    const sentinel = refSentinel.current
    if (!sentinel || postList.length === 0) return
    if (refObserver.current) {
      refObserver.current.disconnect()
    }

    refObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIndexLimitPost((prev) => prev + 3)
        }
      },
      { rootMargin: '0px 0px 100px 0px', threshold: 0 }
    )

    refObserver.current.observe(sentinel)

    return () => {
      if (refObserver.current) {
        refObserver.current.disconnect()
      }
    }
  }, [postList.length])

  return (
    <>
      <Box className={classesGlobal.containerPages}>
        <Box className={classesGlobal.topPageBreadcrumbsSearch}>
          <BreadcrumbsComponent
            crumbs={[
              { label: t('navbar.start'), path: '/' },
              { label: t('news.title'), path: '/news' },
              { label: t(`news.categories.${categoryUrl}`) }
            ]}
          />
          <SearchInput
            redirectUrl="/news/search"
            placeholder={t('news.placeholder-search')}
          />
        </Box>
        <Box>
          <Typography className={classesGlobal.titleSection}>
            {t(`news.categories.${categoryUrl}`)}
          </Typography>
          {isLoading ? (
            <Box className={classes.containerSkeleton}>
              <HorizontalNewsCardSkeleton />
              <Grid container>
                {[...Array(3)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={6} lg={4} key={i}>
                    <ExpandedSkeletonNewsCard />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            postList.length > 0 && (
              <>
                <HorizontalNewsCard
                  idNews={postList[0].id}
                  image={postList[0].path}
                  category={getCategoryRouteByCategoryId(
                    postList[0].newsCategoriesId,
                    categories
                  )}
                  title={postList[0].title}
                  text={postList[0].text}
                />
                <Grid container spacing={2}>
                  {postList.slice(1, indexLimitPost).map((post: any) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={post.id}>
                      <ExpandedNewsItemCard
                        idNews={post.id}
                        image={post.path}
                        category={getCategoryRouteByCategoryId(
                          post.newsCategoriesId,
                          categories
                        )}
                        title={post.title}
                        text={post.text}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )
          )}
          <div ref={refSentinel}></div>
        </Box>
      </Box>
    </>
  )
}

export default NewsListedByCategoryContent
